
import { useI18n } from 'vue-i18n';
import Card from 'primevue/card';
import Button from 'primevue/button';
import Message from 'primevue/message';
import {
  defineComponent, ref, onMounted, computed, onBeforeUnmount, nextTick,
} from 'vue';
import {
  fetchNotifications, markNotificationsAsRead,
} from '@/composables/shop/Notifications';
import { UserState } from '@/composables/User';
import {
  ShopsNotifications,
  ShopsNotificationsReadInputItem,
} from '@/types/generated-types/graphql';
import { showToastError } from '@/helpers';
import Tooltip from 'primevue/tooltip';

interface NotificationCategory {
  value: string;
  label: string;
  icon: string;
  types: string[];
}

export default defineComponent({
  name: 'MyNotifications',
  components: {
    Card,
    Button,
    Message,
  },
  directives: {
    tooltip: Tooltip,
  },
  setup() {
    const { t } = useI18n();
    const notifications: any = ref<ShopsNotifications[]>([]);
    const shopId = UserState?.activeShop?.id ?? 0;
    const paging = ref<number>(0);
    const hasMore = ref(true);
    const loadingHasMore = ref(false);
    const selectedCategory = ref<string>('all');
    const scrollSentinel = ref<HTMLElement | null>(null);
    let observer: IntersectionObserver | null = null;

    const notificationCategories: NotificationCategory[] = [
      {
        value: 'all',
        label: t('notification.all'),
        icon: 'fas fa-list',
        types: ['low', 'normal', 'important'],
      },
      {
        value: 'success',
        label: t('notification.low'),
        icon: 'fas fa-info-circle',
        types: ['low'],
      },
      {
        value: 'warning',
        label: t('notification.normal'),
        icon: 'fas fa-exclamation-circle',
        types: ['normal'],
      },
      {
        value: 'error',
        label: t('notification.high'),
        icon: 'fas fa-exclamation-triangle',
        // types: ['high'],
        types: ['important'],
      },
    ];

    const getNotifications = async () => {
      loadingHasMore.value = true;
      const getNotificationsItems = await fetchNotifications({ shopId, paging: paging.value });
      loadingHasMore.value = false;

      if (getNotificationsItems && getNotificationsItems.length === 0) {
        hasMore.value = false;
        return;
      }

      notifications.value = notifications.value.concat(getNotificationsItems);
    };

    const fetchMore = async () => {
      paging.value += 1;
      await getNotifications();
    };

    const markAllAsRead = async () => {
      const notificationsReadRecords: ShopsNotificationsReadInputItem[] = [];
      notifications.value.forEach((item: ShopsNotifications) => {
        if (item.shopsNotificationsRead?.length === 0) {
          notificationsReadRecords.push({
            id_shop_notification: item.id_shop_notification,
            id_shop: shopId,
            date: new Date(),
          });
        }
      });

      if (notificationsReadRecords.length > 0) {
        await markNotificationsAsRead(notificationsReadRecords);
      }
    };

    const getCategoryForType = (type: string): string => {
      switch (type) {
        case 'low':
          return 'success';
        case 'normal':
          return 'warning';
        // case 'high':
        case 'important':
          return 'error';
        default:
          return 'info';
      }
    };

    const getCategoryLabel = (type: string): string => {
      const category = notificationCategories.find((cat) => cat.types.includes(type));
      return category ? category.label : 'Autre';
    };

    const formatDate = (date: any): string => {
      if (!date) return '';
      const dateObj = new Date(date);
      return dateObj.toLocaleString('fr-FR', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
      });
    };

    const filteredNotifications = computed(() => {
      const selectedCat = notificationCategories.find((cat) => cat.value === selectedCategory.value);
      if (!selectedCat) return notifications.value;
      return notifications.value.filter((notif: ShopsNotifications) => selectedCat.types.includes(notif.type));
    });

    const getCategoryCount = (categoryValue: string): number => {
      const category = notificationCategories.find((cat) => cat.value === categoryValue);
      if (!category) return 0;
      if (categoryValue === 'all') return notifications.value.length;
      return notifications.value.filter((notif: ShopsNotifications) => category.types.includes(notif.type)).length;
    };

    const downloadFile = async (event: Event) => {
      event.preventDefault();

      const apiUrl = (event.currentTarget as HTMLAnchorElement).href;
      const apiToken = 'Ae1i2kndasdcasdfe23sad';

      try {
        const response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'api-token': apiToken,
          },
        });

        if (!response.ok) {
          throw new Error('Failed to download file, invalid token or server error');
        }

        const blob = await response.blob();

        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = 'folie';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } catch (error) {
        console.error('Error:', error);
        await showToastError('Failed to download file.');
      }
    };

    onMounted(async () => {
      await getNotifications();
      await markAllAsRead();

      // Setup Intersection Observer for infinite scroll
      // Use nextTick to ensure DOM is ready
      await nextTick();
      
      if (scrollSentinel.value && hasMore.value) {
        observer = new IntersectionObserver(
          (entries) => {
            const [entry] = entries;
            if (entry.isIntersecting && hasMore.value && !loadingHasMore.value) {
              fetchMore();
            }
          },
          {
            root: null,
            rootMargin: '200px', // Start loading 200px before reaching the sentinel
            threshold: 0,
          },
        );

        observer.observe(scrollSentinel.value);
      }
    });

    onBeforeUnmount(() => {
      if (observer && scrollSentinel.value) {
        observer.unobserve(scrollSentinel.value);
        observer.disconnect();
      }
    });

    return {
      t,
      notifications,
      fetchMore,
      hasMore,
      loadingHasMore,
      downloadFile,
      notificationCategories,
      selectedCategory,
      filteredNotifications,
      getCategoryForType,
      getCategoryLabel,
      formatDate,
      getCategoryCount,
      scrollSentinel,
    };
  },
});
